import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Table, Space, Tag, message, Select, Input, Modal, Form } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import request from '../../../utils/request';
import { useAuth } from '../../../hooks/useAuth';

interface UserRow {
  id: number | string;
  username: string;
  email?: string;
  role: string;
  timezone?: string;
  last_login?: string;
  is_active?: boolean;
  staff_sub_role?: string;
  customer_sub_role?: string;
  assigned_warehouses?: Array<{ id?: number; name?: string; system_code?: string }>;
}

const AdminUserList: React.FC = () => {
  const navigate = useNavigate();
  const { user: adminUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<UserRow[]>([]);
  const [pagination, setPagination] = useState<{ current: number; pageSize: number; total: number }>({ current: 1, pageSize: 20, total: 0 });
  const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
  const [staffSubRole, setStaffSubRole] = useState<string | undefined>(undefined);
  const [keyword, setKeyword] = useState<string>('');
  const [pwdVisible, setPwdVisible] = useState(false);
  const [pwdUser, setPwdUser] = useState<UserRow | null>(null);
  const [form] = Form.useForm();

  const fetchPage = async (page: number, pageSize: number) => {
    setLoading(true);
    try {
      const params: any = { page, page_size: pageSize, non_customer: 1 };
      if (roleFilter) params.role = roleFilter;
      if (staffSubRole) params.staff_sub_role = staffSubRole;
      if (keyword) params.q = keyword;
      const resp = await request.get('/api/v1/users/', { params });
      const body = resp.data;
      const items: any[] = Array.isArray(body) ? body : (Array.isArray(body?.results) ? body.results : []);
      const rows: UserRow[] = items.map((u: any) => ({
        id: u.id,
        username: u.username,
        email: u.email,
        role: u.role,
        timezone: u.timezone,
        last_login: u.last_login,
        is_active: u.is_active,
        staff_sub_role: u.staff_sub_role,
        customer_sub_role: u.customer_sub_role,
        assigned_warehouses: u.assigned_warehouses || [],
      }));
      const total = typeof body?.count === 'number' ? body.count : rows.length;
      setData(rows);
      setPagination(prev => ({ ...prev, current: page, pageSize, total }));
    } catch (e) {
      message.error('获取用户列表失败');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, staffSubRole, keyword]);

  const roleTag = (role: string, staffSub?: string) => {
    const base: Record<string, { color: string; text: string }> = {
      admin: { color: 'red', text: '管理员' },
      staff: { color: 'blue', text: '员工' },
      customer: { color: 'green', text: '客户' },
    };
    const cfg = base[role] || { color: 'default', text: role };
    return (
      <Space size={4}>
        <Tag color={cfg.color}>{cfg.text}</Tag>
        {role === 'staff' && staffSub && (
          <Tag>{staffSub === 'hq_staff' ? '总部员工' : staffSub === 'warehouse_supervisor' ? '仓库主管' : staffSub === 'warehouse_staff' ? '仓库员工' : staffSub}</Tag>
        )}
      </Space>
    );
  };

  const columns: ColumnsType<UserRow> = [
    { title: '用户名', dataIndex: 'username', key: 'username' },
    { title: '角色', dataIndex: 'role', key: 'role', render: (_: any, r) => roleTag(r.role, r.staff_sub_role) },
    { title: '时区', dataIndex: 'timezone', key: 'timezone' },
    { title: '邮箱', dataIndex: 'email', key: 'email' },
    { title: '最后登录(当地时间/管理员时间)', dataIndex: 'last_login', key: 'last_login', render: (v, r) => renderDualTimezone(v, r.timezone) },
    { title: '所属仓库', dataIndex: 'assigned_warehouses', key: 'assigned_warehouses', render: (list?: any[]) => {
      if (!list || list.length === 0) return '-';
      const names = list.map(it => it?.name || it?.system_code || it?.id).filter(Boolean);
      return names.join(', ');
    } },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 220,
      render: (_: any, r) => (
        <Space>
          <Button type="link" onClick={() => { setPwdUser(r); setPwdVisible(true); setTimeout(() => form.resetFields(), 0); }}>重置密码</Button>
          {r.is_active !== false ? (
            <Button type="link" danger onClick={() => handleToggleActive(r, false)}>禁用</Button>
          ) : (
            <Button type="link" onClick={() => handleToggleActive(r, true)}>启用</Button>
          )}
        </Space>
      )
    }
  ];

  // 按指定时区格式化
  const formatWithTZ = (iso?: string, timeZone?: string) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      const opts: Intl.DateTimeFormatOptions = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false, timeZone: timeZone || 'UTC'
      };
      const parts = new Intl.DateTimeFormat('zh-CN', opts).format(d);
      // 格式如 2025/09/16 08:30:00 -> 2025-09-16 08:30:00
      return parts.replaceAll('/', '-');
    } catch {
      return iso;
    }
  };

  // 显示：用户当地时间 / 管理员时间
  const renderDualTimezone = (iso?: string, userTz?: string) => {
    if (!iso) return '-';
    const adminTz = adminUser?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localStr = formatWithTZ(iso, userTz || 'UTC');
    const adminStr = formatWithTZ(iso, adminTz);
    return `${localStr} / ${adminStr}`;
  };

  const handleToggleActive = async (record: UserRow, nextActive: boolean) => {
    try {
      await request.patch(`/api/v1/users/${record.id}/`, { is_active: nextActive });
      message.success(nextActive ? '已启用' : '已禁用');
      fetchPage(pagination.current, pagination.pageSize);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '操作失败');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">系统用户列表</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/dashboard/admin/users/create')}>
          创建系统用户
        </Button>
      </div>
      <div className="bg-white p-4 rounded border mb-3">
        <Space wrap>
          <Select
            allowClear
            placeholder="按角色筛选"
            style={{ width: 180 }}
            value={roleFilter}
            onChange={(v) => { setRoleFilter(v); if (v !== 'staff') setStaffSubRole(undefined); }}
            options={[
              { label: '管理员', value: 'admin' },
              { label: '员工', value: 'staff' },
            ]}
          />
          {roleFilter === 'staff' && (
            <Select
              allowClear
              placeholder="员工子角色"
              style={{ width: 200 }}
              value={staffSubRole}
              onChange={(v) => setStaffSubRole(v)}
              options={[
                { label: '总部员工', value: 'hq_staff' },
                { label: '仓库主管', value: 'warehouse_supervisor' },
                { label: '仓库员工', value: 'warehouse_staff' },
              ]}
            />
          )}
          <Input.Search
            allowClear
            placeholder="搜索用户名/邮箱"
            style={{ width: 260 }}
            onSearch={(v) => setKeyword(v)}
            enterButton
          />
          <Button onClick={() => { setRoleFilter(undefined); setStaffSubRole(undefined); setKeyword(''); fetchPage(1, pagination.pageSize); }}>重置</Button>
        </Space>
      </div>
      <Table
        rowKey={(r) => String(r.id)}
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total, range) => `显示第 ${range[0]} - ${range[1]} 条，共 ${total} 条记录`,
          onChange: (page, pageSize) => fetchPage(page, pageSize),
          onShowSizeChange: (_p, size) => fetchPage(1, size),
        }}
        scroll={{ x: 1100 }}
      />

      <Modal
        title={pwdUser ? `为用户 ${pwdUser.username} 修改密码` : '修改密码'}
        open={pwdVisible}
        onCancel={() => setPwdVisible(false)}
        onOk={() => form.submit()}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" onFinish={async (vals) => {
          if (!pwdUser) return;
          try {
            if (!vals.password || vals.password.length < 6) {
              message.warning('请输入至少6位的新密码');
              return;
            }
            await request.post(`/api/v1/users/${pwdUser.id}/set_password/`, { password: vals.password });
            message.success('密码已更新');
            setPwdVisible(false);
          } catch (e: any) {
            message.error(e?.response?.data?.detail || '更新失败');
          }
        }}>
          <Form.Item label="新密码" name="password" rules={[{ required: true, message: '请输入新密码' }, { min: 6, message: '至少6位' }]}>
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUserList;